// src/pages/CalendarioAziendale.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import { Card, TableCell, TableHeadCell } from '../components/ui';

// const API_BASE = 'http://localhost:3001';
const API_BASE = '/api';

function formatDateKey(date) {
  if (!date) return '';
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const weekdayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export default function CalendarioAziendale() {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [holidays, setHolidays] = useState([]);
  const [justifications, setJustifications] = useState([]);

  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayDescription, setNewHolidayDescription] = useState('');

  const [loading, setLoading] = useState(true);
  const [savingHoliday, setSavingHoliday] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // 🔶 interventi programmati manuali (promemoria)
  const [plannedDays, setPlannedDays] = useState([]);
  const [newPlannedDate, setNewPlannedDate] = useState('');
  const [newPlannedDescription, setNewPlannedDescription] = useState('');
  const [savingPlanned, setSavingPlanned] = useState(false);

  // ✅ lavorazioni programmate dal sistema (da Lavorazioni)
  const [scheduledEvents, setScheduledEvents] = useState([]);
  const [selectedDateKey, setSelectedDateKey] = useState('');

  const monthLabel = useMemo(
    () =>
      currentMonth.toLocaleDateString('it-IT', {
        month: 'long',
        year: 'numeric',
      }),
    [currentMonth]
  );

  /* CARICAMENTO DATI BASE: festività + giustificativi */
  const loadData = async () => {
    setLoading(true);
    setError('');
    setInfo('');

    try {
      const [holRes, jusRes] = await Promise.all([
        authFetch(`${API_BASE}/holidays`),
        authFetch(`${API_BASE}/justificativi?status=approved`),
      ]);

      if (!holRes.ok) throw new Error('Errore nel recupero delle festività');
      if (!jusRes.ok)
        throw new Error('Errore nel recupero dei giustificativi approvati');

      const holidaysData = await holRes.json();
      const justificationsData = await jusRes.json();

      setHolidays(holidaysData || []);
      setJustifications(justificationsData || []);
    } catch (err) {
      console.error('Errore caricamento calendario aziendale:', err);
      setError(
        err.message || 'Errore durante il caricamento del calendario aziendale.'
      );
    } finally {
      setLoading(false);
    }
  };

  /* CARICA LAVORAZIONI PROGRAMMATE DAL BACKEND */
  const loadScheduledEvents = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);

      const from = formatDateKey(monthStart);
      const to = formatDateKey(monthEnd);

      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);

      const res = await authFetch(
        `${API_BASE}/work/calendar?${params.toString()}`
      );
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nel recupero delle lavorazioni programmate.'
        );
      }
      const data = await res.json();
      setScheduledEvents(data || []);
    } catch (err) {
      console.error('Errore caricamento lavorazioni programmate:', err);
      setError(
        err.message || 'Errore nel recupero delle lavorazioni programmate.'
      );
    }
  };

  /* CARICA INTERVENTI PROGRAMMATI MANUALI */
  const loadPlannedDays = async () => {
    try {
      const res = await authFetch(`${API_BASE}/planned-days`);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nel recupero degli interventi programmati.'
        );
      }
      const data = await res.json();
      setPlannedDays(data || []);
    } catch (err) {
      console.error('Errore caricamento interventi programmati:', err);
    }
  };

  useEffect(() => {
    loadData();
    loadPlannedDays();
  }, []);

  useEffect(() => {
    loadScheduledEvents();
    setSelectedDateKey('');
  }, [currentMonth]);

  useEffect(() => {
    if (scheduledEvents.length === 0) {
      setSelectedDateKey('');
      return;
    }
    if (!selectedDateKey) {
      const first = scheduledEvents[0];
      setSelectedDateKey(formatDateKey(first.date));
    }
  }, [scheduledEvents, selectedDateKey]);

  /* COSTRUZIONE MATRICE GIORNI */
  const daysMatrix = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const firstWeekday = (firstDay.getDay() + 6) % 7; // lun=0

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks = [];
    let currentWeek = new Array(firstWeekday).fill(null);

    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(new Date(year, month, day));
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) currentWeek.push(null);
      weeks.push(currentWeek);
    }
    return weeks;
  }, [currentMonth]);

  /* MAP FESTIVITÀ */
  const holidayByDate = useMemo(() => {
    const map = {};
    holidays.forEach((h) => {
      const key = formatDateKey(h.date);
      if (!key) return;
      map[key] = h;
    });
    return map;
  }, [holidays]);

  /* MAP ASSENZE (giustificativi approvati) */
  const absencesByDate = useMemo(() => {
    const map = {};
    justifications.forEach((j) => {
      const start = new Date(j.from);
      const end = new Date(j.to);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = formatDateKey(d);
        if (!map[key]) map[key] = [];
        map[key].push(j);
      }
    });
    return map;
  }, [justifications]);

  /* MAP LAVORAZIONI PROGRAMMATE DAL SISTEMA */
  const scheduledByDate = useMemo(() => {
    const map = {};
    scheduledEvents.forEach((ev) => {
      const key = formatDateKey(ev.date);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [scheduledEvents]);

  /* MAP INTERVENTI PROGRAMMATI MANUALI */
  const plannedByDate = useMemo(() => {
    const map = {};
    plannedDays.forEach((p) => {
      const key = formatDateKey(p.date);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return map;
  }, [plannedDays]);

  const selectedScheduled = useMemo(() => {
    if (!selectedDateKey) return [];
    return scheduledByDate[selectedDateKey] || [];
  }, [scheduledByDate, selectedDateKey]);

  const selectedPlanned = useMemo(() => {
    if (!selectedDateKey) return [];
    return plannedByDate[selectedDateKey] || [];
  }, [plannedByDate, selectedDateKey]);

  const selectedDateLabel = useMemo(() => {
    if (!selectedDateKey) return '';
    const d = new Date(selectedDateKey + 'T00:00:00');
    return d.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }, [selectedDateKey]);

  const changeMonth = (delta) => {
    setCurrentMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + delta);
      return new Date(next.getFullYear(), next.getMonth(), 1);
    });
  };

  /* FESTIVITÀ */
  const handleAddHoliday = async () => {
    setError('');
    setInfo('');

    if (!newHolidayDate) {
      setError('Seleziona una data per la festività.');
      return;
    }

    setSavingHoliday(true);
    try {
      const res = await authFetch(`${API_BASE}/holidays`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: newHolidayDate,
          description:
            newHolidayDescription.trim() || 'Chiusura aziendale',
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore nel salvataggio della festività');
      }

      const created = await res.json();
      setHolidays((prev) => [...prev, created]);
      setNewHolidayDate('');
      setNewHolidayDescription('');
      setInfo('Festività aggiunta correttamente.');
    } catch (err) {
      console.error('Errore aggiunta festività:', err);
      setError(err.message || 'Errore nel salvataggio della festività.');
    } finally {
      setSavingHoliday(false);
    }
  };

  const handleRemoveHoliday = async (id) => {
    setError('');
    setInfo('');
    try {
      const res = await authFetch(`${API_BASE}/holidays/${id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore nella cancellazione.');
      }

      setHolidays((prev) => prev.filter((h) => h._id !== id));
      setInfo('Festività rimossa.');
    } catch (err) {
      console.error('Errore rimozione festività:', err);
      setError(err.message || 'Errore nella cancellazione della festività.');
    }
  };

  /* INTERVENTI PROGRAMMATI MANUALI */
  const handleAddPlanned = async () => {
    setError('');
    setInfo('');

    if (!newPlannedDate || !newPlannedDescription.trim()) {
      setError("Inserisci data e descrizione per l'intervento programmato.");
      return;
    }

    setSavingPlanned(true);
    try {
      const res = await authFetch(`${API_BASE}/planned-days`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: newPlannedDate,
          description: newPlannedDescription.trim(),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || "Errore nel salvataggio dell'intervento programmato."
        );
      }

      const created = await res.json();
      setPlannedDays((prev) => [...prev, created]);
      setNewPlannedDate('');
      setNewPlannedDescription('');
      setInfo('Intervento programmato aggiunto.');
    } catch (err) {
      console.error('Errore aggiunta intervento programmato:', err);
      setError(
        err.message || "Errore nel salvataggio dell'intervento programmato."
      );
    } finally {
      setSavingPlanned(false);
    }
  };

  const handleRemovePlanned = async (id) => {
    setError('');
    setInfo('');
    try {
      const res = await authFetch(`${API_BASE}/planned-days/${id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore nella cancellazione.');
      }

      setPlannedDays((prev) => prev.filter((p) => p._id !== id));
      setInfo('Intervento programmato rimosso.');
    } catch (err) {
      console.error('Errore rimozione intervento programmato:', err);
      setError(err.message || 'Errore nella cancellazione.');
    }
  };

  /* GIUSTIFICATIVI VISIBILI NEL MESE */
  const visibleJustifications = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    monthStart.setHours(0, 0, 0, 0);
    monthEnd.setHours(23, 59, 59, 999);

    return justifications.filter((j) => {
      const from = new Date(j.from);
      const to = new Date(j.to);
      return to >= monthStart && from <= monthEnd;
    });
  }, [justifications, currentMonth]);

  const formatTypeLabel = (type) => {
    if (type === 'manutenzione') return 'Manutenzione';
    if (type === 'intervento') return 'Intervento tecnico';
    return type || '';
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
          Calendario aziendale
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          Gestisci chiusure aziendali, ferie/ROL approvate e interventi/lavorazioni
          programmati.
        </p>

        {loading ? (
          <Card className="shadow-sm p-6 text-sm text-gray-600">
            Caricamento calendario...
          </Card>
        ) : (
          <>
            <div className="grid lg:grid-cols-[1.8fr,1.2fr] gap-6">
              {/* CALENDARIO + DETTAGLIO */}
              <Card className="shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <button
                    type="button"
                    onClick={() => changeMonth(-1)}
                    className="px-2 py-1 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
                  >
                    &larr; Mese prec.
                  </button>
                  <span className="font-semibold text-gray-800 capitalize">
                    {monthLabel}
                  </span>
                  <button
                    type="button"
                    onClick={() => changeMonth(1)}
                    className="px-2 py-1 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
                  >
                    Mese succ. &rarr;
                  </button>
                </div>

                {/* legenda */}
                <div className="flex flex-wrap gap-3 text-[11px] text-gray-500 mb-2">
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-red-100 border border-red-300" />
                    <span>Festività aziendale</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300" />
                    <span>Assenze approvate</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" />
                    <span>Lavorazioni programmate</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-orange-100 border border-orange-300" />
                    <span>Interventi programmati (promemoria)</span>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-500 mb-1">
                  {weekdayLabels.map((d) => (
                    <div key={d} className="py-1">
                      {d}
                    </div>
                  ))}
                </div>

                <div className="space-y-1">
                  {daysMatrix.map((week, wi) => (
                    <div key={wi} className="grid grid-cols-7 gap-1">
                      {week.map((date, di) => {
                        if (!date)
                          return <div key={di} className="h-12 md:h-14" />;

                        const key = formatDateKey(date);
                        const holiday = holidayByDate[key];
                        const absences = absencesByDate[key] || [];
                        const dayEvents = scheduledByDate[key] || [];
                        const planned = plannedByDate[key] || [];

                        const isHoliday = !!holiday;
                        const hasAbsences = absences.length > 0;
                        const hasEvents = dayEvents.length > 0;
                        const hasPlanned = planned.length > 0;
                        const isSelected = key === selectedDateKey;

                        let classes =
                          'h-12 md:h-14 rounded-lg border text-xs flex flex-col items-center justify-center cursor-pointer transition ';
                        if (isHoliday) {
                          classes +=
                            'bg-red-50 border-red-200 text-red-700 font-semibold';
                        } else if (hasPlanned) {
                          classes +=
                            'bg-orange-50 border-orange-200 text-orange-700 font-medium';
                        } else if (hasAbsences) {
                          classes +=
                            'bg-blue-50 border-blue-200 text-blue-700 font-medium';
                        } else if (hasEvents) {
                          classes +=
                            'bg-emerald-50 border-emerald-200 text-emerald-700 font-medium';
                        } else {
                          classes +=
                            'bg-white border-gray-200 text-gray-800 hover:bg-gray-50';
                        }

                        if (isSelected) {
                          classes += ' ring-2 ring-sky-500 ring-offset-1';
                        }

                        return (
                          <button
                            key={di}
                            type="button"
                            className={classes}
                            onClick={() => setSelectedDateKey(key)}
                          >
                            <span>{date.getDate()}</span>
                            {isHoliday && (
                              <span className="text-[9px] mt-0.5">
                                Festività
                              </span>
                            )}
                            {!isHoliday && hasPlanned && (
                              <span className="text-[9px] mt-0.5">
                                {planned.length} int.
                              </span>
                            )}
                            {!isHoliday && !hasPlanned && hasAbsences && (
                              <span className="text-[9px] mt-0.5">
                                {absences.length} ass.
                              </span>
                            )}
                            {!isHoliday &&
                              !hasPlanned &&
                              !hasAbsences &&
                              hasEvents && (
                                <span className="text-[9px] mt-0.5">
                                  {dayEvents.length} lav.
                                </span>
                              )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* DETTAGLIO GIORNO SELEZIONATO */}
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-semibold text-gray-800">
                      Lavorazioni programmate nel giorno selezionato
                    </h2>
                    <span className="text-[11px] text-gray-500 capitalize">
                      {selectedDateLabel || 'Nessun giorno selezionato'}
                    </span>
                  </div>

                  {selectedScheduled.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      Nessuna lavorazione programmata per il giorno selezionato.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full border-collapse text-[11px] md:text-xs">
                        <thead>
                          <tr className="bg-gray-50 text-gray-700">
                            <TableHeadCell className="px-2 py-1 text-left">
                              Cod. interno
                            </TableHeadCell>
                            <TableHeadCell className="px-2 py-1 text-left">
                              Tipo
                            </TableHeadCell>
                            <TableHeadCell className="px-2 py-1 text-left">
                              Cliente
                            </TableHeadCell>
                            <TableHeadCell className="px-2 py-1 text-left">
                              Luogo
                            </TableHeadCell>
                            <TableHeadCell className="px-2 py-1 text-left">
                              Descrizione
                            </TableHeadCell>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedScheduled.map((ev) => (
                            <tr
                              key={ev._id}
                              className="border-b last:border-b-0 hover:bg-gray-50"
                            >
                              <TableCell className="px-2 py-1">
                                {ev.workOrder?.internalCode || '-'}
                              </TableCell>
                              <TableCell className="px-2 py-1 capitalize">
                                {formatTypeLabel(ev.workOrder?.type)}
                              </TableCell>
                              <TableCell className="px-2 py-1">
                                {ev.workOrder?.companyName || '-'}
                              </TableCell>
                              <TableCell className="px-2 py-1">
                                {ev.workOrder?.place || '-'}
                              </TableCell>
                              <TableCell className="px-2 py-1">
                                {ev.workOrder?.description || '-'}
                              </TableCell>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {selectedPlanned.length > 0 && (
                    <div className="mt-3">
                      <h3 className="text-xs font-semibold text-gray-700 mb-1">
                        Interventi programmati (promemoria) nel giorno selezionato
                      </h3>
                      <ul className="text-[11px] text-gray-600 list-disc ml-4 space-y-0.5">
                        {selectedPlanned.map((p) => (
                          <li key={p._id}>{p.description}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Card>

              {/* PANNELLO DESTRO */}
              <div className="space-y-5">
                {/* FESTIVITÀ */}
                <Card className="shadow-sm p-4">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">
                    Festività aziendali
                  </h2>
                  <p className="text-xs text-gray-500 mb-3">
                    Le festività sono visibili a tutti gli utenti e segnano i
                    giorni di chiusura aziendale.
                  </p>

                  <div className="space-y-2 mb-3 text-sm">
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={newHolidayDate}
                        onChange={(e) => setNewHolidayDate(e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                      />
                    </div>
                    <input
                      type="text"
                      value={newHolidayDescription}
                      onChange={(e) =>
                        setNewHolidayDescription(e.target.value)
                      }
                      placeholder="Descrizione (es. Chiusura natalizia)"
                      className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddHoliday}
                      disabled={savingHoliday}
                      className={`mt-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white ${
                        savingHoliday
                          ? 'bg-red-300 cursor-not-allowed'
                          : 'bg-red-500 hover:bg-red-600'
                      }`}
                    >
                      {savingHoliday ? 'Salvataggio...' : 'Aggiungi festività'}
                    </button>
                  </div>

                  {holidays.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      Nessuna festività impostata.
                    </p>
                  ) : (
                    <ul className="space-y-1 text-xs max-h-44 overflow-y-auto pr-1">
                      {holidays
                        .slice()
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((h) => (
                          <li
                            key={h._id}
                            className="flex items-center justify-between border-b last:border-0 py-1"
                          >
                            <div>
                              <span className="font-medium">
                                {new Date(h.date).toLocaleDateString('it-IT')}
                              </span>
                              {h.description && (
                                <span className="ml-1 text-gray-500">
                                  – {h.description}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveHoliday(h._id)}
                              className="text-red-500 hover:text-red-700 text-[11px]"
                            >
                              Rimuovi
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                </Card>

                {/* INTERVENTI PROGRAMMATI MANUALI */}
                <Card className="shadow-sm p-4">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">
                    Interventi programmati
                  </h2>
                  <p className="text-xs text-gray-500 mb-3">
                    Promemoria personali per interventi che vuoi fissare in
                    giorni specifici. Vengono evidenziati in arancione sul
                    calendario.
                  </p>

                  <div className="space-y-2 mb-3 text-sm">
                    <input
                      type="date"
                      value={newPlannedDate}
                      onChange={(e) => setNewPlannedDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none"
                    />
                    <input
                      type="text"
                      value={newPlannedDescription}
                      onChange={(e) =>
                        setNewPlannedDescription(e.target.value)
                      }
                      placeholder="Descrizione intervento / nota"
                      className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddPlanned}
                      disabled={savingPlanned}
                      className={`mt-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white ${
                        savingPlanned
                          ? 'bg-orange-300 cursor-not-allowed'
                          : 'bg-orange-500 hover:bg-orange-600'
                      }`}
                    >
                      {savingPlanned
                        ? 'Salvataggio...'
                        : 'Aggiungi intervento programmato'}
                    </button>
                  </div>

                  {plannedDays.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      Nessun intervento programmato inserito.
                    </p>
                  ) : (
                    <ul className="space-y-1 text-xs max-h-44 overflow-y-auto pr-1">
                      {plannedDays
                        .slice()
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((p) => (
                          <li
                            key={p._id}
                            className="flex items-center justify-between border-b last:border-0 py-1"
                          >
                            <div>
                              <span className="font-medium">
                                {new Date(p.date).toLocaleDateString('it-IT')}
                              </span>
                              {p.description && (
                                <span className="ml-1 text-gray-500">
                                  {' '}
                                  – {p.description}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemovePlanned(p._id)}
                              className="text-orange-500 hover:text-orange-700 text-[11px]"
                            >
                              Rimuovi
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                </Card>

                {/* GIUSTIFICATIVI APPROVATI */}
                <Card className="shadow-sm p-4">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">
                    Ferie / ROL approvate
                  </h2>
                  <p className="text-xs text-gray-500 mb-2">
                    Giustificativi con stato{' '}
                    <span className="font-semibold">APPROVED</span> che
                    interessano il mese visualizzato.
                  </p>

                  {visibleJustifications.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      Nessuna assenza approvata per questo mese.
                    </p>
                  ) : (
                    <ul className="space-y-1 text-xs max-h-52 overflow-y-auto pr-1">
                      {visibleJustifications.map((j) => (
                        <li
                          key={j._id}
                          className="border-b last:border-0 py-1"
                        >
                          <div className="flex justify-between">
                            <span className="font-medium">
                              {j.user?.username || j.user?.email || 'Dipendente'}
                            </span>
                            <span className="capitalize text-gray-600">
                              {j.type}
                            </span>
                          </div>
                          <div className="text-[11px] text-gray-600">
                            {new Date(j.from).toLocaleDateString('it-IT')} –{' '}
                            {new Date(j.to).toLocaleDateString('it-IT')}
                          </div>
                          {j.note && (
                            <div className="text-[11px] text-gray-500 truncate">
                              Note: {j.note}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            </div>

            {/* TABELLA RIEPILOGO LAVORAZIONI DEL MESE */}
            <Card className="mt-6 shadow-sm p-4 md:p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-800">
                  Interventi / lavorazioni programmate nel mese
                </h2>
                <span className="text-xs text-gray-500">
                  Totale: {scheduledEvents.length}
                </span>
              </div>

              {scheduledEvents.length === 0 ? (
                <p className="text-xs md:text-sm text-gray-500">
                  Nessuna lavorazione programmata nel mese corrente.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-[11px] md:text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-700">
                        <TableHeadCell className="px-2 py-1 text-left">
                          Data
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Cod. interno
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Tipo
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Cliente
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Luogo
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Descrizione
                        </TableHeadCell>
                      </tr>
                    </thead>
                    <tbody>
                      {scheduledEvents
                        .slice()
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((ev) => (
                          <tr
                            key={ev._id}
                            className="border-b last:border-b-0 hover:bg-gray-50"
                          >
                            <TableCell className="px-2 py-1">
                              {ev.date
                                ? new Date(ev.date).toLocaleDateString('it-IT')
                                : '-'}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {ev.workOrder?.internalCode || '-'}
                            </TableCell>
                            <TableCell className="px-2 py-1 capitalize">
                              {formatTypeLabel(ev.workOrder?.type)}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {ev.workOrder?.companyName || '-'}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {ev.workOrder?.place || '-'}
                            </TableCell>
                            <TableCell className="px-2 py-1">
                              {ev.workOrder?.description || '-'}
                            </TableCell>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {error && (
              <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            {info && (
              <p className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                {info}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
